/**
 * Which Paperwork rows are documents, and which rows are the same file twice
 * (audit 2026-10-01, job #072651795: 19 rows — 9 of them the tech's test
 * photos already shown in the Photos card, 6 copies of "5849G.jpg", and the PO
 * PDF twice, once as "Closeout upload" and once as "Document", same name, same
 * uploader, same minute).
 *
 * Display only. Nothing is deleted or hidden for good: images get their own
 * "Images" chip, and a collapsed duplicate expands from its "+N copies" link.
 */

export interface PaperworkRowLike {
    id: string;
    file_name: string;
    created_at: string;
    file_size?: number;
    uploaded_by?: string | null;
    uploaded_by_name?: string | null;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i;

/** A photo, not a document — by extension, the only signal on the row. */
export function isImageFile(row: Pick<PaperworkRowLike, "file_name">): boolean {
    return IMAGE_EXT.test(row.file_name.trim());
}

/**
 * Two rows are "the same file" when they carry the same name, came from the
 * same person, landed in the same minute, and (when both sizes are known) are
 * the same size. A row with no uploader is something a workflow generated —
 * those never collapse: #1538's blank-vs-filled report pair shares a name and
 * a date and must stay two rows.
 */
export function duplicateKey(row: PaperworkRowLike): string | null {
    const who = row.uploaded_by ?? row.uploaded_by_name ?? null;
    if (!who) return null;
    const t = new Date(row.created_at).getTime();
    if (Number.isNaN(t)) return null;
    const minute = Math.floor(t / 60_000);
    return [row.file_name.trim().toLowerCase(), who, minute, row.file_size ?? ""].join("|");
}

export interface PaperworkGroup<T> {
    primary: T;
    copies: T[];
}

/** Folds exact duplicates into their first (newest-sorted) row, keeping order. */
export function groupDuplicates<T extends PaperworkRowLike>(rows: T[]): PaperworkGroup<T>[] {
    const groups: PaperworkGroup<T>[] = [];
    const byKey = new Map<string, PaperworkGroup<T>>();
    for (const row of rows) {
        const key = duplicateKey(row);
        const existing = key ? byKey.get(key) : undefined;
        if (existing) {
            existing.copies.push(row);
            continue;
        }
        const group = { primary: row, copies: [] as T[] };
        groups.push(group);
        if (key) byKey.set(key, group);
    }
    return groups;
}
